import { Button } from "primereact/button";
import useIsMobile from "../../../../hooks/useIsMobile";

const stageInfo = {
  active: {
    label: "Sedang diproses",
    className: "bg-indigo-100 text-indigo-700",
    icon: "pi pi-circle-fill text-indigo-500",
    circle: "bg-white text-indigo-600 border-2 border-indigo-500",
  },
  done: {
    label: "Selesai",
    className: "bg-emerald-100 text-emerald-700",
    icon: "pi pi-check-circle text-emerald-500",
    circle: "bg-emerald-500 text-white",
  },
  pending: {
    label: "Belum dimulai",
    className: "bg-slate-100 text-slate-500",
    icon: "pi pi-lock text-slate-400",
    circle: "bg-slate-100 text-slate-400",
  },
  cancelled: {
    label: "Dibatalkan",
    className: "bg-red-100 text-red-600",
    icon: "pi pi-times-circle text-red-400",
    circle: "bg-red-100 text-red-500",
  },
};

// two lines at most, then "..."
const clampTwoLines = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

/**
 * One section (I - VI) of the form TRAN-DOC-003.
 * Closed: a title with a summary. Open: the fields, the signatures and the save button.
 * On a phone the sections hang on a rail (a timeline), so the progress is always in view.
 */
const SectionCard = (props) => {
  const {
    id,
    code,
    title,
    role,
    stage,
    open,
    last,
    onToggle,
    summary,
    saving,
    onSave,
    signatures,
    children,
  } = props;

  const isMobile = useIsMobile();

  const info = stageInfo[stage] ?? stageInfo.pending;
  const dimmed = stage === "pending" || stage === "cancelled";

  const handleOnKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (onToggle) onToggle();
    }
  };

  const chips = (
    <div className="row-start-center flex-wrap gap-2">
      {role && (
        <span className="whitespace-nowrap rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">
          {role}
        </span>
      )}
      <span
        className={`whitespace-nowrap rounded-full px-3 py-0.5 text-xs font-semibold ${info.className}`}
      >
        {info.label}
      </span>
    </div>
  );

  const card = (
    <div
      id={id}
      className={`col-start-start w-full min-w-0 rounded-md bg-white ${
        stage === "active"
          ? "border-2 border-indigo-400"
          : "border border-slate-200"
      } ${dimmed && !open ? "opacity-70" : ""}`}
    >
      {/* header: also the toggle */}
      <div
        className="col-start-start w-full cursor-pointer gap-1 px-3 py-2"
        role="button"
        tabIndex={0}
        aria-expanded={!!open}
        onClick={onToggle}
        onKeyDown={handleOnKeyDown}
      >
        {isMobile ? (
          <>
            <div className="row-between-center w-full gap-3">
              <span className="font-bold text-slate-600">{title}</span>
              <i
                className={`pi ${
                  open ? "pi-chevron-up" : "pi-chevron-down"
                } text-slate-400`}
              />
            </div>
            {open ? (
              chips
            ) : (
              <span
                className="text-sm text-slate-500"
                style={clampTwoLines}
              >
                {summary || info.label}
              </span>
            )}
          </>
        ) : (
          <>
            <div className="row-between-center w-full gap-3">
              <div className="row-start-center min-w-0 gap-2">
                <i className={info.icon} />
                <span
                  className={`font-bold text-slate-600 ${
                    open ? "" : "whitespace-nowrap"
                  }`}
                >
                  {code}. {title}
                </span>
                {!open && summary && (
                  <span className="truncate text-sm text-slate-500">
                    {summary}
                  </span>
                )}
              </div>
              <i
                className={`pi ${
                  open ? "pi-chevron-up" : "pi-chevron-down"
                } text-slate-400`}
              />
            </div>
            {open && <div className="pl-6">{chips}</div>}
          </>
        )}
      </div>

      {open && (
        <div className="col-start-start w-full gap-3 border-t border-slate-100 p-3">
          <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-2">
            {children}
          </div>

          {signatures}

          {stage === "active" && onSave && (
            <div className="row-end-center w-full">
              <Button
                label="Simpan"
                icon="pi pi-save"
                outlined
                loading={saving}
                onClick={onSave}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (!isMobile) return card;

  // phone: a numbered dot on the rail, the line to the next section goes through the gap
  return (
    <div className={`flex w-full items-stretch gap-3 ${last ? "" : "pb-3"}`}>
      <div className="flex w-8 flex-none flex-col items-center">
        <span
          className={`row-center-center h-8 w-8 flex-none rounded-full text-xs font-bold ${info.circle}`}
        >
          {stage === "done" ? (
            <i className="pi pi-check text-xs" />
          ) : stage === "cancelled" ? (
            <i className="pi pi-times text-xs" />
          ) : (
            code
          )}
        </span>
        {!last && (
          <span
            className={`mt-1 w-[2px] flex-1 ${
              stage === "done" ? "bg-emerald-400" : "bg-slate-200"
            }`}
          />
        )}
      </div>
      {card}
    </div>
  );
};

export default SectionCard;
